import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  FormGroup,
  Label,
  PrimaryButton,
  TextButton,
  ButtonGroup,
  ErrorMessage,
} from '../../styles/CommonStyles';
import Modal from '../common/Modal';
import { listRoles, assignRoles } from '../../services/rbacService';
import { useUsageVisibility } from '../../hooks/useUsageVisibility';

// Keys the server's delegation guard protects: ASSIGNING or REVOKING a role
// that carries them requires usage.limits.delegate (rbacAssignRoles delegation
// guard, functions/src/rbac/functions/members.ts). Mirrored here — matching
// RoleEditorModal — so a non-delegate admin sees those roles locked instead of
// toggling freely and hitting a rejected save (owner screenshot 2026-08-12).
const RESTRICTED_DELEGATION_KEYS = new Set(['usage.limits.manage', 'usage.limits.delegate']);

// ── Types ────────────────────────────────────────────────────────────────────

interface AccountMember {
  userId: string;
  email: string;
  displayName?: string;
  assignedRoleIds: string[];
}

interface AccountRole {
  roleId: string;
  name: string;
  description?: string;
  system: boolean;
  status: 'active' | 'disabled';
  grants?: string[];
  effectivePermissions?: string[];
}

interface Props {
  member: AccountMember;
  onClose: () => void;
  onSaved: () => void;
}

// ── Styled components ────────────────────────────────────────────────────────

const MemberHeader = styled.div`
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eee;
`;

const MemberName = styled.div`
  font-weight: 600;
  font-size: 1rem;
  color: var(--brand-black);
`;

const MemberEmail = styled.div`
  font-size: 0.875rem;
  color: var(--brand-text-muted);
  margin-top: 0.125rem;
`;

const RoleCheckRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  cursor: pointer;
  padding: 0.625rem 0.75rem;
  border-radius: 8px;
  transition: background 0.15s ease;
  margin-bottom: 0.375rem;

  &:hover {
    background: rgba(198, 167, 94, 0.05);
  }

  input[type='checkbox'] {
    accent-color: var(--brand-gold);
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    margin-top: 2px;
    cursor: pointer;
  }
`;

const RoleInfo = styled.div``;

// Owner directive 2026-08-18 (task #80): the assign-roles popup must size to
// the screen — mobile through desktop — with no internal scrolling even when
// an organization has many custom roles. Same pattern as RoleEditorModal's
// PermGrid: columns absorb the role count horizontally, collapsing to a
// single column on phones.
const RolesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 0.25rem 1rem;
  align-items: start;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Body = styled.div`
  padding: 1.5rem;

  @media (max-width: 640px) {
    padding: 1rem;
  }
`;

const RoleName = styled.div`
  font-size: 0.9375rem;
  font-weight: 500;
  color: #333;
  text-transform: lowercase;
`;

const RoleDesc = styled.div`
  font-size: 0.8125rem;
  color: var(--brand-text-muted);
  margin-top: 0.1rem;
`;

const SystemTag = styled.span`
  font-size: 0.7rem;
  background: rgba(74, 74, 74, 0.08);
  color: #666;
  border-radius: 20px;
  padding: 0.1rem 0.45rem;
  margin-left: 0.375rem;
  vertical-align: middle;
`;

const LoadingText = styled.div`
  color: var(--brand-text-muted);
  font-size: 0.9rem;
  text-align: center;
  padding: 1.5rem 0;
`;

// ── Component ────────────────────────────────────────────────────────────────

const RoleAssignModal: React.FC<Props> = ({ member, onClose, onSaved }) => {
  const [roles, setRoles] = useState<AccountRole[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set(member.assignedRoleIds));
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { canDelegateLimits } = useUsageVisibility();

  // A role carrying delegation-restricted keys is locked (both directions —
  // granting AND revoking it are delegation changes server-side) for actors
  // without usage.limits.delegate. Everything else stays freely editable, so
  // an admin can do their whole job without tripping the server guard.
  const isDelegationLocked = (role: AccountRole) =>
    !canDelegateLimits &&
    (role.effectivePermissions ?? role.grants ?? []).some(k => RESTRICTED_DELEGATION_KEYS.has(k));

  const anyLocked = roles.some(isDelegationLocked);

  useEffect(() => {
    (async () => {
      try {
        const data = await listRoles();
        setRoles((data.roles as AccountRole[]).filter(r => r.status === 'active'));
      } catch (err: any) {
        setError(err?.message ?? 'failed to load roles');
      } finally {
        setLoadingRoles(false);
      }
    })();
  }, []);

  const toggle = (roleId: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(roleId)) next.delete(roleId); else next.add(roleId);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Drop selections that don't resolve to a fetched active role — e.g. the
      // placeholder 'member' default seeded into assignedRoleIds on accounts
      // whose roles were never created. The server also tolerates dangling
      // carried-over ids now, but sending only real ids keeps the request
      // honest and the save button meaningful.
      const knownRoleIds = new Set(roles.map(r => r.roleId));
      const roleIds = Array.from(selected).filter(id => knownRoleIds.has(id));
      await assignRoles({ userId: member.userId, roleIds });
      onSaved();
    } catch (err: any) {
      setError(err?.message ?? 'save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="assign roles"
      maxWidth="min(1060px, 94vw)"
      noScroll
      footer={
        <ButtonGroup>
          <TextButton onClick={onClose} disabled={saving}>cancel</TextButton>
          <PrimaryButton onClick={handleSave} disabled={saving || loadingRoles}>
            {saving ? 'saving…' : 'save'}
          </PrimaryButton>
        </ButtonGroup>
      }
    >
      <Body>
      <MemberHeader>
        <MemberName>{member.displayName ?? member.email}</MemberName>
        <MemberEmail>{member.email}</MemberEmail>
      </MemberHeader>

      <FormGroup>
        <Label>roles</Label>
        {loadingRoles && <LoadingText>loading roles…</LoadingText>}
        {!loadingRoles && roles.length === 0 && (
          <div style={{ color: '#999', fontSize: '0.9rem' }}>no roles available</div>
        )}
        <RolesGrid>
        {roles.map(role => {
          const locked = isDelegationLocked(role);
          return (
            <RoleCheckRow
              key={role.roleId}
              title={locked ? 'granting or revoking this role requires the "Delegate Usage Limits" permission' : undefined}
              style={locked ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
            >
              <input
                type="checkbox"
                checked={selected.has(role.roleId)}
                disabled={locked}
                onChange={() => toggle(role.roleId)}
              />
              <RoleInfo>
                <RoleName>
                  {role.name}
                  {role.system && <SystemTag>system</SystemTag>}
                  {locked && <SystemTag>owner-managed</SystemTag>}
                </RoleName>
                {role.description && <RoleDesc>{role.description}</RoleDesc>}
                {locked && (
                  <RoleDesc>
                    this role can administer usage limits — only an owner (or a holder of
                    "Delegate Usage Limits") can grant or revoke it.
                  </RoleDesc>
                )}
              </RoleInfo>
            </RoleCheckRow>
          );
        })}
        </RolesGrid>
      </FormGroup>

      {anyLocked && !error && (
        <div style={{ fontSize: '0.8125rem', color: '#888', lineHeight: 1.5, marginTop: '0.25rem' }}>
          roles marked <em>owner-managed</em> stay as they are — every other role saves normally.
        </div>
      )}

      {error && <ErrorMessage>{error}</ErrorMessage>}
      </Body>
    </Modal>
  );
};

export default RoleAssignModal;
